'use client';

import { Button } from '@govinity/ui/components/ui/button';
import {
  DocumentViewer,
  useGetTextContent,
  type ViewableDocument,
} from '@govinity/ui/components/ui-custom/documents-viewer';
import { ChevronRight, X } from 'lucide-react';
import { useEffect } from 'react';
import {
  DocumentTranslationPending,
  useTranslationHold,
} from '@/app/portal/[code]/components/document-translation-pending';
import { DocumentTranslationStatus } from '@/app/portal/[code]/components/document-translation-status';
import { cn } from '@/lib/utils';
import { type TranslatableDocument, useDocumentTranslations } from './translatable-documents';

type MeetingDocumentViewerProps = {
  backLabel?: string;
  onBack?: () => void;
  closeLabel?: string;
  onClose?: () => void;
  compact?: boolean;
  autoRequestTranslation?: boolean;
  subtitle?: string;
  title?: string;
  className?: string;
  document: ViewableDocument & TranslatableDocument;
  /** Free-text query whose tokens are highlighted and scrolled into view (PDF only). */
  searchText?: string;
};

export function MeetingDocumentViewer({
  backLabel,
  onBack,
  closeLabel,
  onClose,
  compact = false,
  autoRequestTranslation = true,
  subtitle,
  title,
  className,
  document,
  searchText,
}: MeetingDocumentViewerProps) {
  const { id, translationStatus } = document;
  const { request, isTranslating } = useDocumentTranslations();
  const { isHeld, release } = useTranslationHold(id, isTranslating(document));

  // Requesting on mount covers every way a reader lands here: card click, deep link, or the
  // agenda auto-opening its only document.
  useEffect(() => {
    if (!autoRequestTranslation) {
      return;
    }
    request({ id, translationStatus });
  }, [autoRequestTranslation, id, translationStatus, request]);

  const { textContent, textError, isLoadingText } = useGetTextContent(isHeld ? null : document);

  return (
    <div className={cn(compact ? 'space-y-1' : 'space-y-4', className)}>
      <div className={cn('flex items-start justify-between gap-2', !compact && 'mb-4')}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className={cn('font-semibold wrap-break-word', compact ? 'text-sm' : 'text-lg')}>
              {title ?? document.name}
            </h2>
            <DocumentTranslationStatus
              status={translationStatus}
              reason={document.translationReason}
            />
          </div>
          {subtitle ? <p className="text-muted-foreground text-sm">{subtitle}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onBack && (
            <Button variant="outline" size="sm" onClick={onBack}>
              <ChevronRight className="h-4 w-4 rotate-180" aria-hidden="true" />
              {backLabel}
            </Button>
          )}
          {onClose && (
            <Button aria-label={closeLabel} onClick={onClose} size="icon-xs" variant="ghost">
              <X aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
      <div className="h-[calc(100vh-56px)]">
        {isHeld ? (
          <DocumentTranslationPending onViewOriginal={release} />
        ) : (
          <DocumentViewer
            allowFullscreen
            document={document}
            textContent={textContent}
            isLoadingText={isLoadingText}
            textError={textError}
            searchText={searchText}
          />
        )}
      </div>
    </div>
  );
}
